import React, { useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Text, Spacing, devices } from '../common';
import { MAX_HEADER_WIDTH } from '../../constants';

type Props = {
  title: string;
  subtitle?: string;
  imageSrc?: string;
  buttonLabel?: string;
  buttonUrl?: string;
};

/* Desktop overlay (logo + 30px, stay left of midline) vs compact overlay
   (top-aligned, full gutter width). Independent of the 1320px header swap. */
const COMPACT_LAYOUT = '(max-width: 1000px)';
/* The mobile header takes over here and uses a 90% row. */
const MOBILE_HEADER = '(max-width: 1320px)';

const SOFT_HYPHEN = '\u00AD';
/* Keep at least this many characters on each side of a soft-hyphen break. */
const MIN_CHARS_EACH_SIDE = 2;

const isHashLink = (url?: string) => !!url && url.startsWith('#');
const isExternal = (url?: string) => !!url && /^https?:\/\//i.test(url);

/**
 * Soft hyphens are invisible unless the line breaks there — then the browser
 * paints a hyphen. Used only for words wider than the line so normal titles
 * still wrap at spaces without dashing.
 */
function withSoftHyphens(word: string): string {
  const match = word.match(/^(\W*)(.*?)(\W*)$/u);
  if (!match) return word;
  const [, lead, core, trail] = match;
  const chars = Array.from(core);
  if (chars.length <= MIN_CHARS_EACH_SIDE * 2) return word;

  let out = '';
  for (let i = 0; i < chars.length; i++) {
    if (i >= MIN_CHARS_EACH_SIDE && chars.length - i >= MIN_CHARS_EACH_SIDE) {
      out += SOFT_HYPHEN;
    }
    out += chars[i];
  }
  return lead + out + trail;
}

/** Words wider than the line must break; soft-hyphenate only those. */
function wordsThatOverflowLine(
  title: string,
  lineWidth: number,
  font: string
): Set<string> {
  const overflowing = new Set<string>();
  if (lineWidth <= 0 || typeof document === 'undefined') return overflowing;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return overflowing;

  ctx.font = font;
  for (const word of title.split(/\s+/).filter(Boolean)) {
    if (ctx.measureText(word).width > lineWidth) {
      overflowing.add(word);
    }
  }
  return overflowing;
}

const HeroBannerOverlay = ({
  title,
  subtitle,
  imageSrc,
  buttonLabel,
  buttonUrl,
}: Props) => {
  const titleRef = useRef<HTMLElement>(null);
  const [hyphenateWords, setHyphenateWords] = useState<Set<string>>(
    () => new Set()
  );

  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return;

    const update = () => {
      const style = getComputedStyle(el);
      const font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      const next = wordsThatOverflowLine(title, el.clientWidth, font);
      setHyphenateWords((prev) => {
        if (
          prev.size === next.size &&
          [...next].every((word) => prev.has(word))
        ) {
          return prev;
        }
        return next;
      });
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [title]);

  const scrollToHash = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isHashLink(buttonUrl)) return;
    e.preventDefault();
    const target = document.getElementById((buttonUrl as string).slice(1));
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const showButton = !!buttonLabel && !!buttonUrl;

  const titleParts = title.split(/(\s+)/).map((part, index) => {
    if (!part || /^\s+$/.test(part)) {
      return <React.Fragment key={index}>{part}</React.Fragment>;
    }
    if (hyphenateWords.has(part)) {
      return (
        <React.Fragment key={index}>{withSoftHyphens(part)}</React.Fragment>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });

  return (
    <Hero $hasImage={!!imageSrc} $imageSrc={imageSrc ?? ''}>
      <HeaderWidthContainer>
        <Content>
          <Title ref={titleRef} variant="title-1" color="white">
            {titleParts}
          </Title>
          {subtitle && (
            <>
              <Spacing amount="medium" axis="y" />
              <Subtitle variant="subtitle" color="white">
                {subtitle}
              </Subtitle>
            </>
          )}
          {showButton && (
            <>
              <Spacing amount="large" axis="y" />
              <BookButton
                href={buttonUrl}
                onClick={scrollToHash}
                {...(isExternal(buttonUrl)
                  ? { target: '_blank', rel: 'noopener noreferrer' }
                  : {})}
              >
                {buttonLabel}
              </BookButton>
            </>
          )}
        </Content>
      </HeaderWidthContainer>
    </Hero>
  );
};

export default HeroBannerOverlay;

const Hero = styled.section<{ $hasImage: boolean; $imageSrc: string }>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: clamp(420px, 62vh, 640px);
  background-color: ${(props) => props.theme.colors.darkBlue};
  background-image: ${(props) =>
    props.$hasImage ? `url(${props.$imageSrc})` : 'none'};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  /* The darkening gradient rides on a pseudo-element so the text stays in normal
     flow: a long title grows the section instead of spilling out of it. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      rgba(11, 35, 65, 0.82) 0%,
      rgba(11, 35, 65, 0.55) 55%,
      rgba(11, 35, 65, 0.25) 100%
    );
  }

  /* Small screens read top-down, so the text sits at the top of the photo rather
     than centered in it. */
  @media ${COMPACT_LAYOUT} {
    align-items: flex-start;
  }

  @media ${devices.mobileXL} {
    min-height: clamp(380px, 70vh, 520px);
  }
`;

/* Repeats the header row's width rules, so its left edge lands on the nav logo's
   and the overlay has something stable to offset from. */
const HeaderWidthContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: ${MAX_HEADER_WIDTH};
  margin: 0 auto;

  @media ${devices.laptopL} {
    width: 95%;
  }

  @media ${MOBILE_HEADER} {
    width: 90%;
  }
`;

const Content = styled.div`
  margin-left: 30px;
  /* Keep the text off the right half of the page: the container is centered, so
     50% of it falls on the page's midline, and the text stops 100px short. */
  max-width: calc(50% - 130px);
  padding: 48px 0;
  text-align: left;

  /* Nothing to make room for once the photo is only as wide as the phone. */
  @media ${COMPACT_LAYOUT} {
    margin-left: 0;
    max-width: none;
  }
`;

const Title = styled(Text)`
  max-width: none;
  /* No automatic hyphenation — wrap at spaces, or at soft hyphens we insert
     into words that are wider than the line (those paint a visible dash). */
  hyphens: manual;
  -webkit-hyphens: manual;
  overflow-wrap: normal;
  word-break: normal;
`;

const Subtitle = styled(Text)`
  max-width: 40ch;
`;

const BookButton = styled.a`
  display: inline-block;
  color: ${(p) => p.theme.colors.darkBlue};
  background-color: ${(p) => p.theme.colors.white};
  border: 1px solid ${(p) => p.theme.colors.white};
  border-radius: 5px;
  padding: 14px 28px;
  font-size: 18px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${(p) => p.theme.colors.lightestBlue};
  }

  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.focus};
    outline-offset: 2px;
  }
`;
